import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, Divider, IconButton, Menu, MenuItem, TableCell, TableRow, Tooltip, Typography, useTheme,
} from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import FormatColorTextOutlinedIcon from '@mui/icons-material/FormatColorTextOutlined';
import { tokens } from '../../theme';
import { formatPhone, personName } from '../../voice/callFormat';
import MemoTaskBadge from '../../components/memoTask/MemoTaskBadge';
import EditableTextCell from './EditableTextCell';
import {
    attemptsText, formatClock, missedLine, formatDateTime, uploadedDay, liveCallExpiresIn, liveCallText, rowTone, summaryText, summaryEdited, originalSummaryText, noSpeechRecognized, handoffLabel, whoLines,
} from './missedCallFormat';

/**
 * 날짜 · 번호 · Who stay put while the rest of the row scrolls sideways under them. `left` is measured from the
 * header cells (the columns size themselves to their content), and the background has to be opaque or the
 * scrolled-away columns show through. Stacking, top to bottom: the header's sticky cells, the rest of the header,
 * the body's sticky cells, everything else.
 */
export const STICKY_COLUMNS = 3;
export const stickyColumnSx = ({ left, last = false, head = false }) => ({
    position: 'sticky',
    left,
    zIndex: head ? 3 : 1,
    backgroundColor: head ? 'background.default' : 'background.paper',
    ...(last && { borderRight: 1, borderRightColor: 'divider' }),
});

export const COLUMNS = [
    { key: 'date', label: '날짜', minWidth: 90 },
    { key: 'seq', label: '번호', minWidth: 48 },
    { key: 'who', label: 'Who', minWidth: 170 },
    { key: 'start', label: '시작시간', minWidth: 90 },
    { key: 'end', label: '종료시간', minWidth: 80 },
    { key: 'summary', label: 'AI 요약', minWidth: 260 },
    { key: 'note', label: '내용', minWidth: 200 },
    { key: 'task', label: '학생카드반영', minWidth: 90 },
    { key: 'owner', label: '담당자', minWidth: 120 },
    { key: 'handoff', label: '담당자 연결', minWidth: 150 },
    { key: 'progress', label: '처리현황', minWidth: 150 },
    { key: 'done', label: '완료', minWidth: 56 },
    { key: 'actions', label: '', minWidth: 130 },
];

const SUMMARY_LINES = 6;
const OWNER_BUTTON_SX = { p: 0, minWidth: 0, textTransform: 'none', display: 'block' };

/**
 * Whether the AI 요약 runs past SUMMARY_LINES, so 더보기 shows exactly when text is cut off. It is measured: how many
 * lines a summary takes depends on the column's width, not on its characters or line breaks — the AI writes a few
 * long sentences that wrap well past six lines in a 260px column. Clamped, the text is taller than its box; open,
 * taller than six lines (kept as it was if the line height can't be read). Re-measured when the text, the state or
 * the cell's width changes.
 */
const useSummaryOverflow = (ref, text, open) => {
    const [overflow, setOverflow] = useState(false);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const measure = () => {
            if (!open) { setOverflow(el.scrollHeight > el.clientHeight + 1); return; }
            const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
            if (Number.isFinite(lineHeight)) setOverflow(el.scrollHeight > lineHeight * SUMMARY_LINES + 1);
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        if (observer) observer.observe(el);
        return () => { if (observer) observer.disconnect(); };
    }, [ref, text, open]);
    return overflow;
};

/** A just-created call counts as live for 90 s; re-render when that runs out (nothing emits then). */
const useLiveExpiry = (row) => {
    const [, setTick] = useState(0);
    const ms = liveCallExpiresIn(row);
    useEffect(() => {
        if (ms == null) return undefined;
        const id = setTimeout(() => setTick((t) => t + 1), ms + 50);
        return () => clearTimeout(id);
    }, [ms]);
};

/**
 * One row of the Missed Calls table (docs/MISSED_CALLS_PLAN.md §6.2). Text color follows the row's tasks; 완료
 * gives every cell a grey background without hiding that color. Deleted rows (the 삭제된 항목 view) are grey and
 * read-only; rows that stopped matching the filters stay, dimmed, until the filters change.
 */
const MissedCallRow = ({
    row, stale = false, stickyLeft = [], meId, currentUser, canCall, callBusy, textMax, manager = false, admins = [],
    onCall, onSms, onHistory, onDelete, onRestore, onMissedTime, onAttachCall, onClaim, onUnclaim, onHandoff, onHandled, onSave, onOpenStudent, onTaskChanged, onWho, onWhoColor, onNewStudent, onEditMemo, locks,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);   // on an edited AI 요약: show the AI's own text
    const summaryRef = useRef(null);
    const [ownerMenu, setOwnerMenu] = useState(null);
    const [handoffMenu, setHandoffMenu] = useState(null);   // 담당자 연결: any admin picks who the row is handed to
    useLiveExpiry(row);

    const tone = rowTone(row);
    const deleted = tone === 'deleted';
    const handled = Boolean(row.is_handled) && !deleted;
    const color = tone === 'red' ? colors.redAccent[500] : tone === 'blue' ? colors.blueAccent[500] : undefined;
    const handledBackground = theme.palette.action.disabledBackground;
    const handledTint = `linear-gradient(${handledBackground}, ${handledBackground})`;
    const hoverTint = `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`;
    const whoColor = row.who_is_red ? colors.redAccent[500] : theme.palette.text.primary;
    const live = liveCallText(row);
    const connected = row.connected_call;
    const edited = summaryEdited(row);
    const summary = edited && showOriginal ? originalSummaryText(row) : summaryText(row);
    const noSpeech = noSpeechRecognized(row);   // a note, not a summary: shown quieter than the AI's text
    const summaryOverflow = useSummaryOverflow(summaryRef, summary, summaryOpen);
    const mine = row.claimed_by?.id != null && row.claimed_by.id === meId;
    const readOnly = deleted;
    const dim = deleted ? 0.55 : stale ? 0.5 : 1;
    const manualWho = row.who?.source === 'manual';
    const save = (field) => (value) => onSave(row, field, value);
    // 내용 · 담당자 연결 · 처리현황 open one writer at a time (plan §5.12).
    const cellLock = (field) => ({
        lockedBy: locks ? locks.heldByOther(row.id, field) : null,
        onClaim: locks ? () => locks.claim(row.id, field) : undefined,
        onRelease: locks ? () => locks.release(row.id, field) : undefined,
        onSaved: locks ? () => locks.forget(row.id, field) : undefined,
    });
    const assign = (adminId) => () => { setOwnerMenu(null); onClaim(row, adminId); };
    const handoff = handoffLabel(row);
    // Same wording as 담당 지정: my own entry reads 나 and comes first. It follows the 담당자 목록 like everyone else.
    const handoffChoices = [...admins.filter((a) => a.id === meId), ...admins.filter((a) => a.id !== meId)]
        .filter((a) => a.id !== row.handoff_to?.id);
    const pickHandoff = (adminId) => () => { setHandoffMenu(null); onHandoff(row, adminId); };

    return (
        <TableRow
            hover
            sx={{
                verticalAlign: 'top',
                '& > td': {
                    color, fontSize: 13, py: 1, opacity: dim,
                    // Keep the completed tint over an opaque base so pinned cells hide scrolled content.
                    ...(handled && { backgroundColor: 'background.paper', backgroundImage: handledTint }),
                },
                // A see-through pinned cell would show the columns scrolling under it, so it dims its content instead.
                '& > td[data-sticky]': {
                    opacity: 1,
                    '& > *': { opacity: dim },
                },
                ...(handled && {
                    '&:hover > td': {
                        backgroundImage: `${hoverTint}, ${handledTint}`,
                    },
                }),
                '&:hover > td[data-sticky]': {
                    backgroundImage: handled ? `${hoverTint}, ${handledTint}` : hoverTint,
                },
            }}
        >
            <TableCell data-sticky sx={stickyColumnSx({ left: stickyLeft[0] })}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>{uploadedDay(row)}</Typography>
                {readOnly || !onMissedTime ? (
                    <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8, whiteSpace: 'nowrap' }}>{missedLine(row, { withDay: true })}</Typography>
                ) : (
                    <Tooltip title="부재 시간 고치기">
                        <Typography
                            variant="caption"
                            onClick={() => onMissedTime(row)}
                            sx={{
                                color: 'inherit', opacity: 0.8, whiteSpace: 'nowrap', cursor: 'pointer',
                                display: 'inline-block', borderBottom: '1px dotted', borderColor: 'divider',
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            {missedLine(row, { withDay: true }) || '부재 시간 입력'}
                        </Typography>
                    </Tooltip>
                )}
                {stale && <Typography variant="caption" display="block" color="text.secondary">필터에 맞지 않음</Typography>}
            </TableCell>
            <TableCell data-sticky sx={stickyColumnSx({ left: stickyLeft[1] })}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>{row.daily_seq}</Typography>
            </TableCell>
            <TableCell data-sticky sx={{ ...stickyColumnSx({ left: stickyLeft[2], last: true }), ...(!deleted && { color: whoColor }) }}>
                {whoLines(row).map((line) => (
                    <Typography
                        key={line.text}
                        variant="body2"
                        onClick={line.student ? () => onOpenStudent(line.student) : undefined}
                        sx={{
                            color: 'inherit',
                            cursor: line.student ? 'pointer' : 'default',
                            '&:hover': line.student ? { textDecoration: 'underline' } : {},
                        }}
                    >
                        {line.text}
                    </Typography>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8, whiteSpace: 'nowrap' }}>{formatPhone(row.phone_number)}</Typography>
                    {!readOnly && onWho && (
                        <Tooltip title={manualWho ? '직접 지정한 Who — 바꾸거나 해제' : '이 번호가 누구인지 지정'}>
                            <IconButton size="small" sx={{ p: 0.25 }} color={manualWho ? 'primary' : 'default'} onClick={() => onWho(row)}>
                                <PersonSearchOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!readOnly && onNewStudent && (
                        <Tooltip title="새 학생을 만들고 이 번호를 학생카드에 저장">
                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onNewStudent(row)}>
                                <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!readOnly && onWhoColor && (
                        <Tooltip title={row.who_is_red ? 'Who를 검정으로' : 'Who를 빨강으로'}>
                            <IconButton
                                size="small"
                                aria-label={row.who_is_red ? 'Who를 검정으로' : 'Who를 빨강으로'}
                                sx={{ p: 0.25, color: row.who_is_red ? colors.redAccent[500] : 'text.primary' }}
                                onClick={() => onWhoColor(row)}
                            >
                                <FormatColorTextOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {connected ? formatClock(connected.started_at) : (attemptsText(row) || '')}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{connected ? formatClock(connected.ended_at) : ''}</TableCell>
            <TableCell>
                {summary && (
                    <>
                        <Typography
                            ref={summaryRef}
                            variant="body2"
                            sx={{
                                color: noSpeech ? 'text.secondary' : 'inherit',
                                ...(noSpeech && { fontStyle: 'italic' }),
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                ...(summaryOpen ? {} : { display: '-webkit-box', WebkitLineClamp: SUMMARY_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
                            }}
                        >
                            {summary}
                        </Typography>
                        {summaryOverflow ? (
                            <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => setSummaryOpen((v) => !v)}>
                                {summaryOpen ? '접기' : '더보기'}
                            </Button>
                        ) : null}
                    </>
                )}
                {edited && (
                    <Button
                        size="small"
                        sx={{ p: 0, minWidth: 0, textTransform: 'none', display: 'block', opacity: 0.8 }}
                        onClick={() => setShowOriginal((v) => !v)}
                    >
                        {showOriginal ? '수정본 보기' : '수정됨 · AI 원문 보기'}
                    </Button>
                )}
                {/* Corrections are saved into the student's memo and shown here; 'AI 원문 보기' still reaches the original. */}
                {!readOnly && onEditMemo && connected?.memo?.id && connected.memo.student_uuid && (
                    <Button
                        size="small"
                        sx={{ p: 0, minWidth: 0, textTransform: 'none', display: 'block' }}
                        onClick={() => onEditMemo(row)}
                    >
                        메모 수정
                    </Button>
                )}
            </TableCell>
            <TableCell>
                <EditableTextCell value={row.note} onSave={save('note')} disabled={readOnly} maxLength={textMax} minWidth={180} {...cellLock('note')} />
            </TableCell>
            <TableCell>
                {connected?.memo && connected.memo.student_uuid ? (
                    <MemoTaskBadge
                        studentUuid={connected.memo.student_uuid}
                        memo={connected.memo}
                        currentUser={currentUser}
                        onChanged={() => onTaskChanged(row)}
                        disabled={readOnly}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">N/A</Typography>
                )}
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ color: 'inherit' }}>{row.claimed_by?.first_name || personName(row.claimed_by) || ''}</Typography>
                {live && <Chip size="small" color="success" icon={<PhoneOutlinedIcon />} label={live} sx={{ mt: 0.5 }} />}
                {!readOnly && (manager ? (
                    <>
                        <Button size="small" sx={OWNER_BUTTON_SX} onClick={(e) => setOwnerMenu(e.currentTarget)}>담당 지정</Button>
                        <Menu anchorEl={ownerMenu} open={Boolean(ownerMenu)} onClose={() => setOwnerMenu(null)} disableEnforceFocus>
                            {!mine && <MenuItem onClick={assign(meId)}>나</MenuItem>}
                            {admins.filter((a) => a.id !== meId && a.id !== row.claimed_by?.id).map((a) => (
                                <MenuItem key={a.id} onClick={assign(a.id)}>{personName(a) || `#${a.id}`}</MenuItem>
                            ))}
                            {row.claimed_by && <Divider />}
                            {row.claimed_by && (
                                <MenuItem onClick={() => { setOwnerMenu(null); onUnclaim(row); }}>담당 해제</MenuItem>
                            )}
                        </Menu>
                    </>
                ) : mine ? (
                    <Button size="small" sx={OWNER_BUTTON_SX} onClick={() => onUnclaim(row)}>담당 해제</Button>
                ) : (
                    <Button size="small" sx={OWNER_BUTTON_SX} onClick={() => onClaim(row)}>담당하기</Button>
                ))}
            </TableCell>
            <TableCell>
                {handoff.text && (
                    <Tooltip title={handoff.legacy ? '예전에 직접 입력한 값입니다 — 선택하면 관리자로 바뀝니다' : ''}>
                        <Typography variant="body2" sx={{ color: handoff.legacy ? 'text.secondary' : 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {handoff.text}
                        </Typography>
                    </Tooltip>
                )}
                {!readOnly && (
                    <>
                        <Button size="small" sx={OWNER_BUTTON_SX} onClick={(e) => setHandoffMenu(e.currentTarget)}>{row.handoff_to ? '변경' : '선택'}</Button>
                        <Menu anchorEl={handoffMenu} open={Boolean(handoffMenu)} onClose={() => setHandoffMenu(null)} disableEnforceFocus>
                            {handoffChoices.map((a) => (
                                <MenuItem key={a.id} onClick={pickHandoff(a.id)}>{a.id === meId ? '나' : (personName(a) || `#${a.id}`)}</MenuItem>
                            ))}
                            {handoff.text && admins.length > 0 && <Divider />}
                            {handoff.text && <MenuItem onClick={pickHandoff(null)}>연결 해제</MenuItem>}
                        </Menu>
                    </>
                )}
            </TableCell>
            <TableCell>
                <EditableTextCell value={row.task_progress} onSave={save('task_progress')} disabled={readOnly} maxLength={textMax} minWidth={140} {...cellLock('task_progress')} />
            </TableCell>
            <TableCell padding="checkbox">
                <Tooltip title={row.is_handled && row.handled_by ? `${personName(row.handled_by)} · ${formatDateTime(row.handled_at)}` : '완료'}>
                    <span>
                        <Checkbox checked={Boolean(row.is_handled)} disabled={readOnly} onChange={(e) => onHandled(row, e.target.checked)} />
                    </span>
                </Tooltip>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {deleted ? (
                    <>
                        <Typography variant="caption" display="block">삭제: {personName(row.deleted_by) || '?'}</Typography>
                        <Typography variant="caption" display="block">{formatDateTime(row.deleted_at)}</Typography>
                        {/* Any admin may put a row back, the same as deleting one. The 번호 is never reused, so it
                            comes back as it was — with its call, AI 요약 and task badge. */}
                        <Button size="small" startIcon={<RestoreOutlinedIcon fontSize="small" />} onClick={() => onRestore(row)} sx={{ mt: 0.5, textTransform: 'none' }}>
                            복구
                        </Button>
                    </>
                ) : (
                    <>
                        {onAttachCall && (
                            <Tooltip title="소프트폰에서 바로 건 통화를 이 행에 연결">
                                <IconButton size="small" onClick={() => onAttachCall(row)}>
                                    <AddLinkIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={!canCall ? '전화 권한이 없습니다' : callBusy ? '통화 중입니다' : '전화 걸기'}>
                            <span>
                                <IconButton size="small" onClick={() => onCall(row)} disabled={!canCall || callBusy}>
                                    <PhoneOutlinedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={row.last_sms_at ? `문자 ${formatDateTime(row.last_sms_at)} (${row.sms_count}회)` : '문자 보내기'}>
                            <IconButton size="small" onClick={() => onSms(row)}>
                                <SmsOutlinedIcon fontSize="small" color={row.last_sms_at ? 'primary' : 'inherit'} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title="기록">
                    <IconButton size="small" onClick={() => onHistory(row)}><HistoryOutlinedIcon fontSize="small" /></IconButton>
                </Tooltip>
                {!deleted && (
                    <Tooltip title="삭제">
                        <IconButton size="small" onClick={() => onDelete(row)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Tooltip>
                )}
                {row.last_sms_at && !deleted && (
                    <Box>
                        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>문자 {formatClock(row.last_sms_at)}</Typography>
                    </Box>
                )}
            </TableCell>
        </TableRow>
    );
};

export default React.memo(MissedCallRow);
